import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/components/ui/use-toast';
import { 
  Calendar, 
  DollarSign, 
  FileText, 
  Tag, 
  User, 
  Clock, 
  Download,
  Edit,
  Send,
  X,
  CheckCircle
} from 'lucide-react';
import ExpenseStatusBadge from './ExpenseStatusBadge';
import expenseService from '@/services/expenseService';
import {
  Expense,
  ExpenseStatus,
  EXPENSE_CATEGORY_INFO,
  EXPENSE_STATUS_INFO,
  SubmitExpenseRequest
} from '@/types/expense';

interface ExpenseDetailsModalProps {
  expense: Expense | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit?: (expense: Expense) => void;
  onSubmit?: (expense: Expense) => void;
  onRefresh?: () => void;
}

const ExpenseDetailsModal: React.FC<ExpenseDetailsModalProps> = ({
  expense,
  isOpen,
  onClose,
  onEdit,
  onSubmit,
  onRefresh
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  if (!expense) return null;

  const categoryInfo = EXPENSE_CATEGORY_INFO[expense.category];
  const statusInfo = EXPENSE_STATUS_INFO[expense.status];

  const handleSubmitExpense = async () => {
    if (!expense.receiptUrl) return;

    try {
      setIsSubmitting(true);
      const submitData: SubmitExpenseRequest = {};
      
      await expenseService.submitExpense(expense.id, submitData);
      
      toast({
        title: 'Success',
        description: 'Expense submitted for approval',
      });
      
      onSubmit?.(expense);
      onRefresh?.();
      onClose();
    } catch (error: any) {
      console.error('Error submitting expense:', error);
      toast({
        title: 'Error',
        description: 'Failed to submit expense. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = () => {
    onEdit?.(expense);
    onClose();
  };

  const handleViewReceipt = async () => {
    try {
      await expenseService.downloadReceipt(expense.id);
    } catch (error: any) {
      console.error('Error viewing receipt:', error);
      toast({
        title: 'Error',
        description: 'Failed to view receipt. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const canEdit = expense.status === ExpenseStatus.DRAFT;
  const canSubmit = expense.status === ExpenseStatus.DRAFT && expense.receiptUrl;

  const formatAmount = (amount: string) => {
    return expenseService.formatAmount(amount);
  };

  const formatDate = (dateString: string) => {
    return expenseService.formatDate(dateString);
  };

  const formatDateTime = (dateString: string) => {
    return expenseService.formatDateTime(dateString);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[900px] max-w-[95vw] max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Expense Details</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Status and Amount Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ExpenseStatusBadge status={expense.status} size="lg" />
              {expense.status === ExpenseStatus.REJECTED && expense.rejectionReason && (
                <Badge variant="outline" className="text-red-600 border-red-200">
                  Rejected
                </Badge>
              )}
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold">{formatAmount(expense.amount)}</div>
              <div className="text-sm text-muted-foreground">Total Amount</div>
            </div>
          </div>

          <Separator />

          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Basic Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  <span>Expense Date</span>
                </div>
                <div className="font-medium">{formatDate(expense.expenseDate)}</div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Tag className="h-4 w-4" />
                  <span>Category</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-base">{categoryInfo.icon}</span>
                  <span className="font-medium">{categoryInfo.label}</span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <FileText className="h-4 w-4" />
                <span>Description</span>
              </div>
              <div className="font-medium bg-muted/50 p-3 rounded-md">
                {expense.description}
              </div>
            </div>
          </div>

          <Separator />

          {/* Receipt Information */}
          {(expense.receiptUrl || expense.receiptFilename) && (
            <>
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Receipt</h3>
                
                <div className="bg-muted/50 p-4 rounded-md">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      {expense.receiptFilename && (
                        <div className="font-medium">{expense.receiptFilename}</div>
                      )}
                      {expense.receiptUrl && (
                        <div className="text-sm text-muted-foreground">
                          Receipt available
                        </div>
                      )}
                    </div>
                    {expense.receiptUrl && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleViewReceipt}
                        className="gap-2"
                      >
                        <Download className="h-4 w-4" />
                        View Receipt
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              <Separator />
            </>
          )}

          {/* Status Timeline */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Status Timeline</h3>
            
            <div className="space-y-3">
              {/* Created */}
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-blue-500" />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Created</span>
                    <span className="text-sm text-muted-foreground">
                      {formatDateTime(expense.createdAt)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Submitted */}
              {expense.submittedAt && (
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-yellow-500" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Submitted for Approval</span>
                      <span className="text-sm text-muted-foreground">
                        {formatDateTime(expense.submittedAt)}
                      </span>
                    </div>
                    {expense.submittedByEmail && (
                      <div className="text-sm text-muted-foreground">
                        by {expense.submittedByEmail}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Reviewed */}
              {expense.reviewedAt && (
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${
                    expense.status === ExpenseStatus.APPROVED ? 'bg-green-500' : 'bg-red-500'
                  }`} />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">
                        {expense.status === ExpenseStatus.APPROVED ? 'Approved' : 'Rejected'}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {formatDateTime(expense.reviewedAt)}
                      </span>
                    </div>
                    {expense.reviewedByEmail && (
                      <div className="text-sm text-muted-foreground">
                        by {expense.reviewedByEmail}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Invoiced */}
              {expense.addedToInvoiceAt && (
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-blue-600" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Added to Invoice</span>
                      <span className="text-sm text-muted-foreground">
                        {formatDateTime(expense.addedToInvoiceAt)}
                      </span>
                    </div>
                    {expense.invoiceLineItemId && (
                      <div className="text-sm text-muted-foreground">
                        Invoice Line Item #{expense.invoiceLineItemId}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Rejection Reason */}
          {expense.status === ExpenseStatus.REJECTED && expense.rejectionReason && (
            <>
              <Separator />
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-red-600">Rejection Reason</h3>
                <div className="bg-red-50 border border-red-200 p-4 rounded-md">
                  <p className="text-red-800">{expense.rejectionReason}</p>
                </div>
              </div>
            </>
          )}

          {/* Notes */}
          {expense.notes && (
            <>
              <Separator />
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Notes</h3>
                <div className="bg-muted/50 p-3 rounded-md">
                  {expense.notes}
                </div>
              </div>
            </>
          )}

          {/* Next Steps */}
          {expense.status === ExpenseStatus.DRAFT && (
            <>
              <Separator />
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-md">
                <h4 className="font-medium text-blue-800 mb-2">Next Steps</h4>
                <p className="text-blue-700 text-sm">
                  This expense is in draft status. You can edit the details or submit it for client approval.
                </p>
              </div>
            </>
          )}

          {expense.status === ExpenseStatus.PENDING_APPROVAL && (
            <>
              <Separator />
              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-md">
                <h4 className="font-medium text-yellow-800 mb-2">Pending Approval</h4>
                <p className="text-yellow-700 text-sm">
                  Your expense has been submitted and is waiting for client approval.
                </p>
              </div>
            </>
          )}

          {expense.status === ExpenseStatus.APPROVED && !expense.addedToInvoiceAt && (
            <>
              <Separator />
              <div className="bg-green-50 border border-green-200 p-4 rounded-md">
                <h4 className="font-medium text-green-800 mb-2">Approved</h4>
                <p className="text-green-700 text-sm">
                  Your expense has been approved! You can now add it to an invoice.
                </p>
              </div>
            </>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-between pt-6 border-t">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          
          <div className="flex gap-2">
            {canEdit && onEdit && (
              <Button
                variant="outline"
                onClick={handleEdit}
                className="gap-2"
              >
                <Edit className="h-4 w-4" />
                Edit
              </Button>
            )}
            
            {canSubmit && (
              <Button
                onClick={handleSubmitExpense}
                disabled={isSubmitting}
                className="gap-2"
              >
                <Send className="h-4 w-4" />
                {isSubmitting ? 'Submitting...' : 'Submit for Approval'}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExpenseDetailsModal; 